import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, useTheme } from '@/App';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import NotificationBell from '@/components/NotificationBell';
import { ThemeSelector } from '@/components/ThemeSelector';
import KeyboardShortcuts from '@/components/common/KeyboardShortcuts';
import FavoritesPanel from '@/components/common/FavoritesPanel';
import { 
  Activity, LayoutDashboard, FileInput, ClipboardList, Lightbulb, FileText, 
  LogOut, Menu, Sun, Moon, Monitor, ChevronDown, ChevronLeft, ChevronRight,
  ClipboardCheck, Sparkles, Brain, Settings2, TrendingUp, Upload, Shield,
  Lock, LayoutGrid, Users, Zap, BarChart3, Calendar, UserPlus,
  ClipboardCheck as ClipboardCheckSquare, UserMinus, Clock, Mail, Trophy,
  GraduationCap, Database, Palette, FileSpreadsheet, HeartPulse, MessageSquare,
  Stethoscope, Heart, DollarSign, Award, Search, Command as CommandIcon,
  PanelLeftClose, PanelLeft, AlertTriangle, FileCheck, UserCircle, Building,
  PieChart, Gauge, RefreshCw, Calculator, Target, Settings, Flame
} from 'lucide-react';

// Navigation organized by category
const navigationGroups = [
  {
    title: 'Overview',
    items: [
      { name: 'Dashboard', href: '/', icon: LayoutDashboard },
      { name: 'Operations Center', href: '/operations-center', icon: Brain, badge: 'NEW' },
      { name: 'Command Center', href: '/command-center', icon: Gauge, badge: 'COO' },
      { name: 'ROI & One-Pager', href: '/roi', icon: Calculator, badge: 'New' },
      { name: 'Strategic Plan', href: '/strategic-plan', icon: Target, badge: 'New' },
      { name: 'AI Assistant', href: '/ai-agent', icon: MessageSquare, badge: 'AI' },
    ]
  },
  {
    title: 'Intelligence',
    items: [
      { name: 'CareFlow Intelligence', href: '/careflow-intelligence', icon: Zap, badge: 'AI' },
      { name: 'Document Intelligence', href: '/document-intelligence', icon: FileText, badge: 'AI' },
      { name: 'Growth Intelligence', href: '/growth-intelligence', icon: TrendingUp, badge: 'AI' },
      { name: 'Patient Acuity', href: '/patient-acuity', icon: Activity, badge: 'New' },
      { name: 'Predictive Outcomes', href: '/predictive-outcomes', icon: AlertTriangle, badge: 'AI' },
      { name: 'Ask Your Data', href: '/ask-data', icon: MessageSquare, badge: 'AI' },
      { name: 'AI Intelligence', href: '/ai-intelligence', icon: Brain, badge: 'New' },
      { name: 'Workforce Intelligence', href: '/workforce-intelligence', icon: Users },
      { name: 'Census Predictions', href: '/census-predictions', icon: TrendingUp, badge: 'AI' },
      { name: 'AI Simulations', href: '/ai-simulations', icon: Sparkles, badge: 'New' },
    ]
  },
  {
    title: 'Quick Actions',
    items: [
      { name: 'Intelligent Scheduling', href: '/intelligent-scheduling', icon: Calendar, badge: 'AI' },
      { name: 'Team Recognition', href: '/team-recognition', icon: Award, badge: 'New' },
      { name: 'Staff Management', href: '/staff-management', icon: Users, badge: 'New' },
      { name: 'Labor Utilization', href: '/labor-utilization', icon: Clock, badge: 'AI' },
      { name: 'PIP Intelligence', href: '/pip-intelligence', icon: Brain, badge: 'AI' },
      { name: 'Clinical QA Analyzer', href: '/clinical-qa', icon: FileText, badge: 'AI' },
      { name: 'Goal Tracker', href: '/goals', icon: Target, badge: 'New' },
      { name: 'Hospice Excellence', href: '/hospice-excellence', icon: Award, badge: 'CMS' },
    ]
  },
  {
    title: 'Integrations',
    items: [
      { name: 'EHR Integration', href: '/ehr-integration', icon: Database, badge: 'New' },
    ]
  },
  {
    title: 'QAPI Modules',
    items: [
      { name: 'Clinical Quality', href: '/clinical-quality', icon: Stethoscope },
      { name: 'Patient Experience', href: '/patient-experience', icon: Heart },
      { name: 'Financial Health', href: '/financial-health', icon: DollarSign },
      { name: 'CHAP Readiness', href: '/chap-readiness', icon: Award },
    ]
  },
  {
    title: 'Compliance',
    items: [
      { name: 'Compliance Dashboard', href: '/compliance-dashboard', icon: ClipboardCheckSquare },
      { name: 'HIPAA Compliance', href: '/hipaa-compliance', icon: Shield, badge: 'HIPAA' },
      { name: 'Survey Readiness', href: '/survey-readiness', icon: ClipboardCheck },
      { name: 'Audit-Ready View', href: '/audit-ready', icon: FileCheck, badge: 'New' },
      { name: 'Compliance Settings', href: '/compliance', icon: Lock },
    ]
  },
  {
    title: 'Operations',
    items: [
      { name: 'RNCM Productivity', href: '/rncm-productivity', icon: Users },
      { name: 'Capacity Projection', href: '/capacity-projection', icon: Calculator, badge: 'New' },
      { name: 'Burnout Monitoring', href: '/burnout-monitoring', icon: Flame, badge: 'New' },
      { name: 'Burnout Risk', href: '/burnout-dashboard', icon: HeartPulse },
      { name: 'PTO Calendar', href: '/pto-calendar', icon: Calendar },
      { name: 'Hiring Predictions', href: '/hiring-predictions', icon: UserPlus, badge: 'AI' },
      { name: 'Admissions/Discharges', href: '/admissions-discharges', icon: UserMinus },
    ]
  },
  {
    title: 'Data & Reports',
    items: [
      { name: 'Monthly Financials', href: '/monthly-financials', icon: DollarSign, badge: 'New' },
      { name: 'Weekly Reports', href: '/weekly-reports', icon: Calendar, badge: 'Auto' },
      { name: 'KPI Performance Center', href: '/kpi-performance-center', icon: PieChart, badge: 'New' },
      { name: 'KPI Sync Monitor', href: '/kpi-sync-monitor', icon: RefreshCw },
      { name: 'Data Entry', href: '/data-entry', icon: FileInput },
      { name: 'Batch Import', href: '/batch-import', icon: Upload },
      { name: 'Reports', href: '/reports', icon: FileText },
      { name: 'Export Reports', href: '/export-reports', icon: FileSpreadsheet },
      { name: 'National Benchmarks', href: '/benchmarking', icon: BarChart3 },
    ]
  },
  {
    title: 'PIPs',
    items: [
      { name: 'Active PIPs', href: '/pips', icon: ClipboardList },
      { name: 'Auto-PIP', href: '/auto-pip', icon: Zap, badge: 'AI' },
      { name: 'Execute PIPs', href: '/execute-pips', icon: Zap },
      { name: 'PIP Predictions', href: '/pip-predictions', icon: Brain, badge: 'AI' },
    ]
  },
  {
    title: 'Settings',
    items: [
      { name: 'Data Admin', href: '/data-admin', icon: Database, badge: 'Admin' },
      { name: 'System Config', href: '/system-config', icon: Settings, badge: 'New' },
      { name: 'System Health', href: '/system-health', icon: Activity, badge: 'Audit' },
      { name: 'System Alerts', href: '/alerts', icon: AlertTriangle, badge: 'New' },
      { name: 'Data Health', href: '/data-health', icon: Database, badge: 'New' },
      { name: 'Data History', href: '/data-history', icon: Clock, badge: 'New' },
      { name: 'Digest Settings', href: '/digest-settings', icon: Clock },
      { name: 'Email Notifications', href: '/email-settings', icon: Mail },
      { name: 'Automated Import', href: '/automated-import', icon: RefreshCw, badge: 'New' },
      { name: 'Team Leaderboard', href: '/leaderboard', icon: Trophy },
      { name: 'Training Center', href: '/training', icon: GraduationCap },
      { name: 'EHR Integration', href: '/ehr-integration', icon: Database },
      { name: 'Dashboard Settings', href: '/dashboard-settings', icon: LayoutGrid },
    ]
  },
];

// Admin-only navigation
const adminNavigation = [
  { name: 'User Management', href: '/user-management', icon: Users, badge: 'Admin' },
  { name: 'Security', href: '/security', icon: Lock },
];

// Quick search commands
const quickCommands = [
  { name: 'Find patient', action: 'search-patient', icon: UserCircle, description: 'Search patient records' },
  { name: 'Run compliance risk scan', action: 'compliance-scan', icon: Shield, description: 'Check compliance status' },
  { name: 'Generate CTI summary', action: 'cti-summary', icon: FileCheck, description: 'Create CTI documentation' },
  { name: 'Show RN productivity', action: 'rn-productivity', icon: TrendingUp, description: 'View productivity trends' },
  { name: 'Check burnout risk', action: 'burnout-check', icon: HeartPulse, description: 'Analyze staff burnout' },
  { name: 'CHAP readiness score', action: 'chap-score', icon: Award, description: 'View audit readiness' },
];

const Sidebar = ({ collapsed, setCollapsed, mobile = false, onClose, user }) => {
  const location = useLocation();
  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';
  
  return (
    <div className={`flex flex-col h-full ${mobile ? '' : 'border-r border-slate-200/50 dark:border-slate-800/50'} 
                    ${collapsed && !mobile ? 'w-20' : 'w-72'} transition-all duration-300
                    bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl`}>
      {/* Logo - Goodwin Hospice */}
      <div className="p-4 border-b border-slate-200/50 dark:border-slate-800/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#00b0ab] to-[#008a86] flex items-center justify-center 
                           shadow-lg shadow-[#00b0ab]/20 relative overflow-hidden shrink-0 group">
              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#00F0FF]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <Activity className="h-6 w-6 text-white relative z-10" />
            </div>
            {(!collapsed || mobile) && (
              <div className="animate-fade-in">
                <h1 className="font-bold text-lg font-heading leading-tight text-slate-900 dark:text-white">
                  Goodwin Hospice
                </h1>
                <p className="text-[11px] text-muted-foreground font-medium">CareLens Intelligence</p>
              </div>
            )}
          </div>
          {!mobile && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setCollapsed(!collapsed)}
              className="h-8 w-8 shrink-0 hover:bg-[#00b0ab]/10 transition-colors"
              data-testid="sidebar-toggle-btn"
            >
              {collapsed ? <PanelLeft className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
            </Button>
          )}
        </div>
      </div>
      
      {/* Navigation */}
      <ScrollArea className="flex-1 px-3 py-4">
        <nav className="space-y-6">
          {navigationGroups.map((group) => (
            <div key={group.title}>
              {(!collapsed || mobile) && (
                <h3 className="nav-section-title text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-3 mb-2">
                  {group.title}
                </h3>
              )}
              <div className="space-y-1">
                {group.items.map((item) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <NavLink
                      key={item.name}
                      to={item.href}
                      onClick={onClose}
                      className={`nav-link group ${isActive ? 'active' : ''} ${collapsed && !mobile ? 'justify-center px-2' : ''}`}
                      data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                      title={collapsed ? item.name : undefined}
                    >
                      <item.icon className={`h-5 w-5 shrink-0 transition-all duration-200 
                        ${isActive 
                          ? 'text-[#00b0ab] dark:text-[#00F0FF]' 
                          : 'text-slate-500 group-hover:text-slate-900 dark:group-hover:text-slate-200'
                        }`} 
                      />
                      {(!collapsed || mobile) && (
                        <>
                          <span className="flex-1 truncate">{item.name}</span>
                          {item.badge && (
                            <Badge variant="secondary" className="nav-badge text-[10px] px-1.5 py-0 
                              bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-0
                              group-hover:bg-[#00b0ab]/10 group-hover:text-[#00b0ab] dark:group-hover:text-[#00F0FF] transition-colors">
                              {item.badge}
                            </Badge>
                          )}
                        </>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}
          
          {/* Admin Navigation */}
          {isAdmin && (
            <div>
              {(!collapsed || mobile) && (
                <h3 className="nav-section-title text-[10px] font-semibold text-red-500 dark:text-red-400 uppercase tracking-wider px-3 mb-2">
                  Administration
                </h3>
              )}
              <div className="space-y-1">
                {adminNavigation.map((item) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <NavLink
                      key={item.name}
                      to={item.href}
                      onClick={onClose}
                      className={`nav-link group ${isActive ? 'active' : ''} ${collapsed && !mobile ? 'justify-center px-2' : ''}`}
                      data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <item.icon className={`h-5 w-5 shrink-0 transition-colors ${isActive ? 'text-primary' : 'text-slate-500 group-hover:text-slate-900 dark:group-hover:text-slate-200'}`} />
                      {(!collapsed || mobile) && (
                        <>
                          <span className="flex-1 truncate">{item.name}</span>
                          {item.badge && (
                            <Badge variant="destructive" className="nav-badge text-[10px] px-1.5 py-0">
                              {item.badge}
                            </Badge>
                          )}
                        </>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          )}
        </nav>
      </ScrollArea>
      
      {/* Footer */}
      <div className="p-4 border-t border-slate-200/50 dark:border-slate-800/50 bg-slate-50/50 dark:bg-slate-900/50">
        <div className={`flex items-center gap-2 text-xs text-muted-foreground ${collapsed && !mobile ? 'justify-center' : ''}`}>
          <div className="h-2 w-2 rounded-full bg-[#00F0FF] animate-pulse shadow-[0_0_10px_rgba(0,240,255,0.5)] shrink-0" />
          {(!collapsed || mobile) && (
            <span className="font-medium">System Online</span>
          )}
        </div>
        {(!collapsed || mobile) && (
          <p className="text-[10px] text-muted-foreground mt-1">CMS / CHAP Compliant</p>
        )}
      </div>
    </div>
  );
};

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  
  // Keyboard shortcut for command bar
  useEffect(() => {
    const down = (e) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setCommandOpen((open) => !open);
      }
    };
    
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);
  
  const handleCommandSelect = useCallback((action) => {
    setCommandOpen(false);
    switch (action) {
      case 'search-patient': navigate('/ehr-integration'); break;
      case 'compliance-scan': navigate('/compliance-dashboard'); break;
      case 'cti-summary': navigate('/clinical-quality'); break;
      case 'rn-productivity': navigate('/rncm-productivity'); break;
      case 'burnout-check': navigate('/burnout-dashboard'); break;
      case 'chap-score': navigate('/chap-readiness'); break;
      default: break;
    }
  }, [navigate]);
  
  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';
  };
  
  const getRoleBadge = () => {
    switch (user?.role) {
      case 'super_admin':
        return <Badge className="role-badge role-badge-executive">Executive</Badge>;
      case 'admin':
        return <Badge className="role-badge role-badge-admissions">Admin</Badge>;
      default:
        return <Badge className="role-badge role-badge-rncm">Staff</Badge>;
    }
  };

  return (
    <div className="min-h-screen flex bg-background grid-pattern">
      {/* Desktop Sidebar */}
      <aside className={`hidden lg:flex flex-col fixed inset-y-0 z-50 transition-all duration-300 ${sidebarCollapsed ? 'w-20' : 'w-72'}`}>
        <Sidebar 
          collapsed={sidebarCollapsed} 
          setCollapsed={setSidebarCollapsed}
          user={user} 
        />
      </aside>
      
      {/* Mobile Sidebar */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="left" className="p-0 w-72 border-r border-slate-200/50 dark:border-slate-800/50">
          <Sidebar mobile onClose={() => setMobileMenuOpen(false)} user={user} />
        </SheetContent>
      </Sheet>
      
      {/* Command Dialog - Spotlight Style */}
      <CommandDialog open={commandOpen} onOpenChange={setCommandOpen}>
        <CommandInput placeholder="Search commands, pages, or actions..." className="border-0" />
        <CommandList className="max-h-[400px]">
          <CommandEmpty>No results found.</CommandEmpty>
          <CommandGroup heading="Quick Actions">
            {quickCommands.map((cmd) => (
              <CommandItem
                key={cmd.action}
                onSelect={() => handleCommandSelect(cmd.action)}
                className="cursor-pointer py-3"
              >
                <div className="h-9 w-9 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center mr-3">
                  <cmd.icon className="h-4 w-4 text-[#00b0ab]" />
                </div>
                <div className="flex flex-col">
                  <span className="font-medium">{cmd.name}</span>
                  <span className="text-xs text-muted-foreground">{cmd.description}</span>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
          <CommandSeparator />
          <CommandGroup heading="Navigate">
            {navigationGroups.slice(0, 3).flatMap(g => g.items).map((item) => (
              <CommandItem
                key={item.href}
                onSelect={() => {
                  setCommandOpen(false);
                  navigate(item.href);
                }}
                className="cursor-pointer"
              >
                <item.icon className="mr-3 h-4 w-4 text-muted-foreground" />
                <span>{item.name}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </CommandDialog>
      
      {/* Main Content */}
      <div className={`flex-1 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-72'}`}>
        {/* Header - Glass Style */}
        <header className="sticky top-0 z-40 glass-panel">
          <div className="flex items-center justify-between h-16 px-4 lg:px-6">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => setMobileMenuOpen(true)}
                className="lg:hidden"
                data-testid="mobile-menu-btn"
              >
                <Menu className="h-5 w-5" />
              </Button>
              
              {/* Command Bar - Futuristic */}
              <button
                onClick={() => setCommandOpen(true)}
                className="command-bar hidden sm:flex"
                data-testid="command-bar-btn"
              >
                <Search className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Search anything...</span>
                <kbd className="ml-auto pointer-events-none hidden h-6 select-none items-center gap-1 rounded-md 
                               bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 
                               px-2 font-mono text-[10px] font-medium sm:flex">
                  <span className="text-xs">⌘</span>K
                </kbd>
              </button>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Role Badge */}
              <div className="hidden md:block">
                {getRoleBadge()}
              </div>
              
              {/* Favorites Panel */}
              <FavoritesPanel />
              
              {/* Notification Bell */}
              <NotificationBell />
              
              {/* Theme Selector */}
              <ThemeSelector />
              
              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="flex items-center gap-2 px-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors" 
                    data-testid="user-menu-btn"
                  >
                    <Avatar className="h-9 w-9 ring-2 ring-slate-200 dark:ring-slate-700">
                      <AvatarFallback className="bg-gradient-to-br from-[#00b0ab] to-[#008a86] text-white text-sm font-semibold">
                        {getInitials(user?.name)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden sm:inline text-sm font-medium">{user?.name}</span>
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 glass-card">
                  <DropdownMenuLabel>
                    <div className="flex flex-col">
                      <span className="font-semibold">{user?.name}</span>
                      <span className="text-xs text-muted-foreground font-normal">{user?.email}</span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={logout} 
                    className="text-destructive focus:text-destructive focus:bg-destructive/10 cursor-pointer"
                    data-testid="logout-btn"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>
        
        {/* Page Content */}
        <main className="p-4 lg:p-6 xl:p-8">
          <Outlet />
        </main>
        
        {/* Global Keyboard Shortcuts */}
        <KeyboardShortcuts enabled={true} />
      </div>
    </div>
  );
}
